import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectActiveProjectId } from "../../../store/projectSlice";
import {
  cwListProjects,
  cwGetProjectList,
  cwMarkAttendance,
} from "../../../services/contractWorkers";
import CameraCapture from "./CameraCapture";

// ─────────────────────────────────────────────────────────────────────────────
// Contract Workers → Attendance
// The logged-in user picks a project + date, captures ONE live group photo,
// and ticks the workers who are present. Everyone ticked is recorded as a full
// day. If a worker leaves during working hours, the supervisor "outs" him from
// the Daily Roster (turns that day into a half day).
// ─────────────────────────────────────────────────────────────────────────────

// Local calendar date (YYYY-MM-DD), NOT the UTC date — toISOString() slices
// to UTC, which rolls back to "yesterday" for any IST time before 5:30am.
// That mismatch was making attendance marked early morning file under the
// previous day while the Daily Roster (opened later, once UTC has caught up)
// defaulted to "today" and showed nothing.
const todayStr = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const ContractWorkerAttendance = () => {
  const activeProjectId = useSelector(selectActiveProjectId);

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(activeProjectId || "");
  const [date, setDate] = useState(todayStr());
  const [mode, setMode] = useState("group"); // "group" | "individual"
  // Individual tab only: "staff" = another employee punches the worker (live
  // photo or gallery, as before); "self" = the worker punches himself with a
  // front-camera selfie only (no gallery upload).
  const [punchSource, setPunchSource] = useState("staff");
  const [workers, setWorkers] = useState([]);
  const [checked, setChecked] = useState({});
  const [photo, setPhoto] = useState(null);
  // { [workerId]: "full" | "half" | "leave" } — already punched today, locked.
  const [recorded, setRecorded] = useState({});
  // Punch remarks: typed per worker (remarks), already saved (savedRemarks).
  const [remarks, setRemarks] = useState({});
  const [savedRemarks, setSavedRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    cwListProjects()
      .then((res) => setProjects(res.data?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  const load = useCallback(() => {
    if (!projectId) {
      setWorkers([]);
      setChecked({});
      return;
    }
    setLoading(true);
    cwGetProjectList(projectId, date)
      .then((res) => {
        const d = res.data || {};
        const ws = d.workers || [];
        setWorkers(ws);
        // Always start clean — never pre-fill from a previous save.
        setChecked({});
        setRemarks({});
        setRecorded(d.markedStatuses || {});
        setSavedRemarks(d.markedRemarks || {});
      })
      .catch(() => {
        setWorkers([]);
        setChecked({});
        setRemarks({});
        setRecorded({});
        setSavedRemarks({});
      })
      .finally(() => setLoading(false));
  }, [projectId, date]);

  useEffect(() => {
    load();
  }, [load]);

  // Group mode → multi-select; Individual mode → exactly one worker.
  // A worker already punched today is locked — no re-punch.
  const toggle = (id) => {
    if (recorded[id]) return;
    setChecked((prev) =>
      mode === "individual"
        ? { [id]: !prev[id] }
        : { ...prev, [id]: !prev[id] },
    );
  };

  const changePunchSource = (src) => {
    setPunchSource(src);
    setPhoto(null); // a gallery photo must not carry over into selfie-only
  };

  const selfieOnly = mode === "individual" && punchSource === "self";

  const changeMode = (m) => {
    setMode(m);
    if (m === "individual") {
      const first = Object.keys(checked).find((k) => checked[k]);
      setChecked(first ? { [first]: true } : {});
    }
  };

  const selectedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked],
  );

  const pendingCount = useMemo(
    () => workers.filter((w) => !recorded[w._id]).length,
    [workers, recorded],
  );

  const save = async () => {
    if (!projectId) {
      Swal.fire({ icon: "warning", title: "Select a project first." });
      return;
    }
    if (!photo) {
      Swal.fire({
        icon: "warning",
        title: "Photo required",
        text:
          mode === "individual"
            ? selfieOnly
              ? "Take your selfie to punch attendance."
              : "Capture a photo of the worker."
            : "Capture one group photo of the workers present.",
      });
      return;
    }
    const ids = Object.keys(checked).filter((k) => checked[k]);
    if (!ids.length) {
      Swal.fire({ icon: "warning", title: "Tick at least one worker." });
      return;
    }
    if (mode === "individual" && ids.length !== 1) {
      Swal.fire({
        icon: "warning",
        title: "Individual attendance takes exactly one worker.",
      });
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("projectId", projectId);
      fd.append("date", date);
      fd.append("mode", mode);
      fd.append("punchSource", selfieOnly ? "self" : "staff");
      fd.append("groupPhoto", photo);
      ids.forEach((id) => fd.append("workerIds", id));
      fd.append(
        "entries",
        JSON.stringify(
          ids.map((id) => ({ workerId: id, status: "full", remarks: (remarks[id] || "").trim() })),
        ),
      );

      const res = await cwMarkAttendance(fd, projectId);
      Swal.fire({
        icon: "success",
        title: `Attendance saved — ${res.data?.markedCount ?? ids.length} present`,
        timer: 1500,
        showConfirmButton: false,
      });
      setPhoto(null);
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to save attendance.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Attendance" motherMenu="Contract Workers" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <h4 className="card-title mb-0 me-auto">Mark Attendance</h4>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${mode === "group" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => changeMode("group")}
            >
              Group
            </button>
            <button
              type="button"
              className={`btn ${mode === "individual" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => changeMode("individual")}
            >
              Individual
            </button>
          </div>
          <select
            className="form-select form-select-sm"
            style={{ width: 210 }}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName}
              </option>
            ))}
          </select>
          <input
            type="date"
            max={todayStr()}
            className="form-control form-control-sm"
            style={{ width: 160 }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="card-body">
          {Object.keys(recorded).length > 0 && (
            <div className="alert alert-info py-2 small">
              Workers already punched for {date} are locked — you can't punch
              them again. To out a worker who left early, use the Daily Roster.
            </div>
          )}

          {mode === "individual" && (
            <div className="btn-group btn-group-sm mb-3" role="group">
              <button
                type="button"
                className={`btn ${punchSource === "self" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => changePunchSource("self")}
              >
                <i className="fa fa-user me-1" /> Worker punches own (selfie only)
              </button>
              <button
                type="button"
                className={`btn ${punchSource === "staff" ? "btn-success" : "btn-outline-success"}`}
                onClick={() => changePunchSource("staff")}
              >
                <i className="fa fa-users me-1" /> Other employee (camera / gallery)
              </button>
            </div>
          )}

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">
                {selfieOnly
                  ? "Selfie*"
                  : mode === "individual"
                    ? "Worker Photo*"
                    : "Group Photo*"}
              </label>
              <CameraCapture
                key={selfieOnly ? "selfie" : "standard"}
                disabled={saving}
                allowGallery={!selfieOnly}
                facing={selfieOnly ? "user" : "environment"}
                onCapture={(file) => setPhoto(file)}
              />
              <small className="text-muted d-block mt-1">
                {selfieOnly
                  ? "Selfie only — take a live photo with the front camera; gallery upload is not allowed."
                  : "Take a live photo or upload one from your gallery."}
              </small>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-bold d-flex justify-content-between">
                <span>Workers on this project</span>
                <span className="text-muted">{selectedCount} selected</span>
              </label>
              {loading ? (
                <div className="text-center py-4">Loading…</div>
              ) : workers.length === 0 ? (
                <div className="text-muted py-4 text-center">
                  No active workers on this project.
                </div>
              ) : (
                <div
                  className="table-responsive"
                  style={{ maxHeight: 420, overflowY: "auto" }}
                >
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: 44 }} aria-label="Select" />
                        <th>Worker</th>
                        <th>Profile</th>
                        <th>Shift</th>
                        <th style={{ minWidth: 200 }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workers.map((w) => {
                        const lockStatus = recorded[w._id];
                        return (
                        <tr
                          key={w._id}
                          style={{
                            cursor: lockStatus ? "default" : "pointer",
                            opacity: lockStatus ? 0.6 : 1,
                          }}
                          onClick={() => toggle(w._id)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            {lockStatus ? (
                              <i className="fa fa-lock text-muted" />
                            ) : (
                              <input
                                type={
                                  mode === "individual" ? "radio" : "checkbox"
                                }
                                name="cw-attendance-worker"
                                className="form-check-input"
                                checked={!!checked[w._id]}
                                onChange={() => toggle(w._id)}
                              />
                            )}
                          </td>
                          <td className="fw-semibold text-capitalize">
                            {w.name}
                            {w.contractCompany ? (
                              <small className="d-block text-muted">
                                {w.contractCompany}
                              </small>
                            ) : null}
                          </td>
                          <td>{w.workProfile}</td>
                          <td>
                            {w.shift || "Morning"}
                            {lockStatus ? (
                              <span
                                className={`badge ms-2 bg-${
                                  lockStatus === "half"
                                    ? "warning"
                                    : lockStatus === "leave"
                                      ? "info"
                                      : "success"
                                }`}
                              >
                                Punched
                                {lockStatus !== "full" ? ` · ${lockStatus}` : ""}
                              </span>
                            ) : null}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {lockStatus ? (
                              <small className="text-muted">
                                {savedRemarks[w._id] || "—"}
                              </small>
                            ) : (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Optional remark"
                                maxLength={300}
                                value={remarks[w._id] || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setRemarks((p) => ({ ...p, [w._id]: v }));
                                  // Typing a remark ticks the worker.
                                  if (v && !checked[w._id]) toggle(w._id);
                                }}
                              />
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <small className="text-muted">
                Tick who is present (recorded as a full day). If a worker leaves
                during working hours, out him from the Daily Roster.
              </small>
            </div>
          </div>

          {workers.length > 0 && pendingCount === 0 && (
            <div className="text-success small mt-2">
              Every worker on this project is already punched for {date}.
            </div>
          )}
          <button
            className="btn btn-primary mt-2"
            disabled={saving || pendingCount === 0}
            onClick={save}
          >
            {saving ? "Saving…" : "Save Attendance"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ContractWorkerAttendance;
